/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { getUserInfo } from '@/reducer/userSlice';
import { RootState } from '@/store';
import { getValue } from '@testing-library/user-event/dist/utils';
import React, { Suspense, lazy, useEffect, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setDataLayer } from '@/utils/Gtm';

import useWindowSize from '@/hooks/useWindowSize';

const Home = lazy(() => import('./home'));
const Customer = lazy(() => import('./customer/Customer'));
const Payment = lazy(() => import('./payment/Payment'));
const Subscribe = lazy(() => import('./payment/subscribe'));
const PaymentFormComponent = lazy(() => import('./payment/paymentFormComponent'));
const Billing = lazy(() => import('./billing'));
const PaymentResult = lazy(() => import('./payment/paymentFormComponent/paymentResult'));
const CustomPlan = lazy(() => import('./customer/customComponent/customPlan'));
const ContactInfo = lazy(() => import('./customer/customComponent/contactInfo/index'));
const PromotionCode = lazy(() => import('./customer/customComponent/promotionCode/index'));

const MobilePricing = lazy(() => import('../mobile/pages/Pricing'));
const MobilePayment = lazy(() => import('../mobile/pages/payment/Payment'));
const MobileSubscribe = lazy(() => import('../mobile/pages/payment/subscribe'));
const MobilePaymentFormComponent = lazy(() => import('../mobile/pages/payment/paymentFormComponent'));
const MobilePaymentResult = lazy(() => import('../mobile/pages/payment/paymentFormComponent/paymentResult'));

const MobileCustomPlan = lazy(() => import('../mobile/pages/customer/customComponent/customPlan'));
const MobileUpdatePaymentMethod = lazy(
  () => import('../mobile/pages/customer/customComponent/customPlan/paymentMethod/UpdatePaymentMethod'),
);
const MobileCustomer = lazy(() => import('../mobile/pages/customer/Customer'));

export default function Router() {
  const { isMobile } = useWindowSize();

  return (
    <BrowserRouter>
      <Suspense fallback={<div />}>
        {isMobile ? (
          <Routes>
            <Route path="pricing" element={<MobilePricing />} />
            <Route
              path="products"
              element={
                <LoginExamination
                  Component={() => {
                    return <MobilePayment />;
                  }}
                />
              }
            >
              <Route path="offerselection" element={<MobileSubscribe />} />
              <Route path="checkout" element={<MobilePaymentFormComponent />} />
              <Route path="checkout/successfulorder" element={<MobilePaymentResult />} />
            </Route>
            <Route
              path="personalcentre"
              element={
                <LoginExamination
                  Component={() => {
                    return <MobileCustomer />;
                  }}
                />
              }
            >
              <Route path="myplans" element={<MobileCustomPlan />} />
              <Route path="paymentmethod" element={<MobileUpdatePaymentMethod />} />
            </Route>
            <Route
              path="/personalcentre/myplans/billing/:id"
              element={
                <LoginExamination
                  Component={() => {
                    return <Billing />;
                  }}
                />
              }
            />
          </Routes>
        ) : (
          <Routes>
            <Route path="pricing" element={<Home />} />
            <Route
              path="products"
              element={
                <LoginExamination
                  Component={() => {
                    return <Payment />;
                  }}
                />
              }
            >
              <Route path="offerselection" element={<Subscribe />} />
              <Route path="checkout" element={<PaymentFormComponent />} />
              <Route path="checkout/successfulorder" element={<PaymentResult />} />
            </Route>
            <Route
              path="personalcentre"
              element={
                <LoginExamination
                  Component={() => {
                    return <Customer />;
                  }}
                />
              }
            >
              <Route path="myplans" element={<CustomPlan />} />
              <Route path="contact" element={<ContactInfo />} />
              <Route path="promocode" element={<PromotionCode />} />
            </Route>
            <Route
              path="/personalcentre/myplans/billing/:id"
              element={
                <LoginExamination
                  Component={() => {
                    return <Billing />;
                  }}
                />
              }
            />
          </Routes>
        )}
      </Suspense>
    </BrowserRouter>
  );
}

// eslint-disable-next-line no-undef
function LoginExamination(props: { Component: () => JSX.Element }) {
  const { Component } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const { pathname, search } = location;
  const nextPath = encodeURIComponent(pathname + search);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  useEffect(() => {
    (async () => {
      if (userInfo.id) {
        setDataLayer({ user_id: userInfo.id });
        setLoading(false);
        return;
      }
      const _dispatch = dispatch as any;
      try {
        const apiRes = await _dispatch(getUserInfo({})).unwrap();
        setLoading(false);
        setDataLayer({ user_id: apiRes.id });
      } catch (error) {
        window.location.href = '/webtool/#/login?next=' + nextPath;
        setLoading(false);
      }
    })();
  }, []);
  if (loading) {
    return <div>loading...</div>;
  }
  if (!userInfo.id) {
    window.location.href = '/webtool/#/login?next=' + nextPath;
    return null;
  } else {
    return Component();
  }
}
